import { Navigate, Route, Routes } from 'react-router-dom';

import { LoginPage } from '@/auth/LoginPage';
import { ProtectedRoute } from '@/auth/RoleGuard';
import { AppShell } from '@/layouts/AppShell';
import { BillDetailPage } from '@/pages/bills/BillDetailPage';
import { BillsListPage } from '@/pages/bills/BillsListPage';
import { BranchesPage } from '@/pages/branches/BranchesPage';
import { BrandsPage } from '@/pages/brands/BrandsPage';
import { CategoriesPage } from '@/pages/categories/CategoriesPage';
import { Customer360Page } from '@/pages/customers/Customer360Page';
import { CustomersListPage } from '@/pages/customers/CustomersListPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { EmployeesListPage } from '@/pages/employees/EmployeesListPage';
import { ExpensesListPage } from '@/pages/expenses/ExpensesListPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { BulkImportPage } from '@/pages/products/BulkImportPage';
import { ProductDetailPage } from '@/pages/products/ProductDetailPage';
import { ProductsListPage } from '@/pages/products/ProductsListPage';
import { AnalyticsPage } from '@/pages/reports/AnalyticsPage';
import { DayEndPage } from '@/pages/reports/DayEndPage';
import { GstReportPage } from '@/pages/reports/GstReportPage';
import { InventoryValuePage } from '@/pages/reports/InventoryValuePage';
import { ReportsHubPage } from '@/pages/reports/ReportsHubPage';
import { RevenueReportPage } from '@/pages/reports/RevenueReportPage';
import { ProfilePage } from '@/pages/settings/ProfilePage';
import { SettingsHubPage } from '@/pages/settings/SettingsHubPage';
import { SubscriptionPage } from '@/pages/settings/SubscriptionPage';
import { ThemePage } from '@/pages/settings/ThemePage';
import { LowStockPage } from '@/pages/stock/LowStockPage';
import { StockLevelsPage } from '@/pages/stock/StockLevelsPage';
import { StockMovementsPage } from '@/pages/stock/StockMovementsPage';
import { SuppliersListPage } from '@/pages/suppliers/SuppliersListPage';

/**
 * Single source of truth for the panel's URL surface. Roles:
 *   - owner: every route
 *   - manager: ops + read of reports (no employees/branches, no settings writes)
 *   - accountant (CA): bills + reports + GST + exports (read-only feel)
 *
 * The base path is `/panel/*` (set by Laravel's catch-all + Vite's base
 * config). React Router's basename is set in `App.tsx`.
 */
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route element={<ProtectedRoute />}>
        <Route element={<AppShell />}>
          <Route index element={<DashboardPage />} />

          {/* Sales */}
          <Route element={<ProtectedRoute allow={['owner', 'manager', 'accountant']} />}>
            <Route path="bills" element={<BillsListPage />} />
            <Route path="bills/:id" element={<BillDetailPage />} />
          </Route>
          <Route element={<ProtectedRoute allow={['owner', 'manager']} />}>
            <Route path="customers" element={<CustomersListPage />} />
            <Route path="customers/:id" element={<Customer360Page />} />
          </Route>

          {/* Inventory */}
          <Route element={<ProtectedRoute allow={['owner', 'manager']} />}>
            <Route path="products" element={<ProductsListPage />} />
            <Route path="products/bulk-import" element={<BulkImportPage />} />
            <Route path="products/:id" element={<ProductDetailPage />} />
            <Route path="stock" element={<StockLevelsPage />} />
            <Route path="stock/movements" element={<StockMovementsPage />} />
            <Route path="stock/low" element={<LowStockPage />} />
            <Route path="categories" element={<CategoriesPage />} />
            <Route path="brands" element={<BrandsPage />} />
            <Route path="suppliers" element={<SuppliersListPage />} />
          </Route>

          {/* Money */}
          <Route element={<ProtectedRoute allow={['owner', 'manager', 'accountant']} />}>
            <Route path="expenses" element={<ExpensesListPage />} />
            <Route path="reports" element={<ReportsHubPage />} />
            <Route path="reports/analytics" element={<AnalyticsPage />} />
            <Route path="reports/revenue" element={<RevenueReportPage />} />
            <Route path="reports/inventory" element={<InventoryValuePage />} />
            <Route path="reports/day-end" element={<DayEndPage />} />
          </Route>
          <Route element={<ProtectedRoute allow={['owner', 'accountant']} />}>
            <Route path="reports/gst" element={<GstReportPage />} />
          </Route>

          {/* Owner-only */}
          <Route element={<ProtectedRoute allow={['owner']} />}>
            <Route path="branches" element={<BranchesPage />} />
            <Route path="employees" element={<EmployeesListPage />} />
            <Route path="subscription" element={<SubscriptionPage />} />
            <Route path="settings" element={<SettingsHubPage />} />
            <Route path="settings/profile" element={<ProfilePage />} />
            <Route path="settings/theme" element={<ThemePage />} />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>

      {/* Top-level catch-all — bounces unknown deep links to login or home. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
